import type { GrammarError } from '$lib/types/grammar'
import { findTextPosition } from './textCorrection'

export interface TextSegment {
  text: string
  error?: GrammarError
  inlineView?: {
    id: string
    suggested: string
    mode: 'paraphrase' | 'expand' | 'shrink' | 'continue' | 'prompt'
    status?: string
  }
  searchMatch?: {
    isCurrent: boolean
  }
}

/**
 * CSS properties to copy from textarea to overlay for perfect alignment
 */
export const TEXTAREA_STYLE_PROPS = [
  'boxSizing',
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'border',
  'borderTop',
  'borderRight',
  'borderBottom',
  'borderLeft',
  'borderWidth',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'fontSize',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'fontVariant',
  'fontKerning',
  'fontFeatureSettings',
  'lineHeight',
  'letterSpacing',
  'wordSpacing',
  'textIndent',
  'textAlign',
  'textTransform',
  'textRendering',
  'whiteSpace',
  'wordWrap',
  'wordBreak',
  'overflowWrap',
  'direction',
  'writingMode',
  'tabSize'
] as const

/**
 * Find accurate position of error text in the full text
 * Uses the unified findTextPosition from textCorrection utils
 */
export function findErrorPosition(
  text: string,
  error: GrammarError
): { startPos: number; endPos: number } | null {
  // Get original text from either field
  let originalText = error.original || error.originalText
  if (!originalText) return null
  
  // Trim trailing whitespace from original text for better matching
  const trimmedOriginal = originalText.trimEnd()
  const trailingSpaces = originalText.length - trimmedOriginal.length
  
  // Get position from position field
  const startPos = error.position?.start ?? 0
  const endPos = error.position?.end ?? 0
  
  // Check if position is already correct (with trimmed text)
  const textAtPosition = text.slice(startPos, startPos + trimmedOriginal.length)
  if (textAtPosition === trimmedOriginal) {
    return { 
      startPos, 
      endPos: startPos + trimmedOriginal.length // Use actual text length, not including trailing spaces
    }
  }
  
  // Use unified position finder with larger search radius for errors
  // Try with trimmed text first for better accuracy
  let position = findTextPosition(text, trimmedOriginal, startPos, 100)
  
  if (!position && trimmedOriginal !== originalText) {
    // If trimmed search failed, try with original text
    position = findTextPosition(text, originalText, startPos, 100)
  }
  
  if (position) {
    return {
      startPos: position.start,
      endPos: position.end
    }
  }
  
  return null
}

/**
 * Create text segments with error highlights and inline view suggestions
 */
export function createTextSegments(
  text: string,
  errors: GrammarError[],
  inlineViews?: Array<{
    id: string
    position: { start: number; end: number }
    suggestedText?: string
    mode: 'paraphrase' | 'expand' | 'shrink' | 'continue'
    status: string
  }>,
  searchHighlights?: Array<{
    start: number
    end: number
    type: 'search'
    isCurrent: boolean
  }>
): TextSegment[] {
  if (!text) {
    return [{ text }]
  }
  
  const segments: TextSegment[] = []
  const usedPositions = new Set<string>()
  
  // Combine errors, inline views, and search matches into a single array
  const allHighlights: Array<{
    startPos: number
    endPos: number
    error?: GrammarError
    inlineView?: { id: string; suggested: string; mode: 'paraphrase' | 'expand' | 'shrink' | 'continue' | 'prompt'; status?: string }
    searchMatch?: { isCurrent: boolean }
  }> = []
  
  // First, collect inline views (they have priority over errors)
  const inlineViewPositions = new Set<string>()
  
  // Add inline views to highlights (including processing state without suggestions yet)
  if (inlineViews && inlineViews.length > 0) {
    // Include views that are pending, processing, or completed (but not applied or cancelled)
    const activeViews = inlineViews.filter(v => 
      v.status !== 'applied' && v.status !== 'cancelled'
    )
    
    for (const view of activeViews) {
      const posKey = `${view.position.start}-${view.position.end}`
      inlineViewPositions.add(posKey)
      
      allHighlights.push({
        startPos: view.position.start,
        endPos: view.position.end,
        inlineView: {
          id: view.id,
          suggested: view.suggestedText || '',
          mode: view.mode,
          status: view.status
        }
      })
    }
  }
  
  // Add search matches
  if (searchHighlights && searchHighlights.length > 0) {
    for (const match of searchHighlights) {
      allHighlights.push({
        startPos: match.start,
        endPos: match.end,
        searchMatch: {
          isCurrent: match.isCurrent
        }
      })
    }
  }
  
  // Then add errors (but skip if there's an inline view at the same position)
  if (errors && errors.length > 0) {
    const errorsWithPositions = errors
    .map(error => {
      const position = findErrorPosition(text, error)
      if (!position) {
        // Silently skip errors that can't be positioned
        return null
      }
      return { ...error, ...position }
    })
    .filter((e): e is GrammarError & { startPos: number; endPos: number } => e !== null)
    
    // Only add errors that don't overlap with inline views
    for (const e of errorsWithPositions) {
      const posKey = `${e.startPos}-${e.endPos}`
      if (!inlineViewPositions.has(posKey)) {
        allHighlights.push({ 
          startPos: e.startPos, 
          endPos: e.endPos, 
          error: e 
        })
      }
    }
  }
  
  // Sort all highlights by position
  const sortedHighlights = allHighlights.sort((a, b) => a.startPos - b.startPos)
  
  let lastEnd = 0
  
  for (const highlight of sortedHighlights) {
    const posKey = `${highlight.startPos}-${highlight.endPos}`
    if (usedPositions.has(posKey)) continue
    usedPositions.add(posKey)
    
    // Skip if overlapping with previous highlight
    if (highlight.startPos < lastEnd) {
      // Silently skip overlapping highlights
      continue
    }
    
    // Add text before this highlight
    if (highlight.startPos > lastEnd) {
      segments.push({
        text: text.slice(lastEnd, highlight.startPos)
      })
    }
    
    // Add the highlight segment - ensure we don't go beyond text length
    const segmentEnd = Math.min(highlight.endPos, text.length)
    const segmentText = text.slice(highlight.startPos, segmentEnd)
    
    if (highlight.error) {
      segments.push({
        text: segmentText,
        error: highlight.error
      })
    } else if (highlight.inlineView) {
      segments.push({
        text: segmentText,
        inlineView: highlight.inlineView
      })
    } else if (highlight.searchMatch) {
      segments.push({
        text: segmentText,
        searchMatch: highlight.searchMatch
      })
    }
    
    lastEnd = segmentEnd
  }
  
  // Add remaining text
  if (lastEnd < text.length) {
    segments.push({
      text: text.slice(lastEnd)
    })
  }
  
  return segments
}

/**
 * Get CSS class for error highlighting
 */
export function getErrorHighlightClass(
  error: GrammarError,
  isHovered: boolean = false
): string {
  const classes = ['error-highlight']
  
  // Check if user kept original (shows in orange)
  if (error.isOriginalKept || error.userAction === 'kept-original') {
    classes.push('error-level-kept-original')
  } else {
    // Use normal error level
    const level = error.level || 'error'
    switch (level) {
      case 'error':
        classes.push('error-level-error')
        break
      case 'warning':
        classes.push('error-level-warning')
        break
      case 'suggestion':
        classes.push('error-level-suggestion')
        break
    }
  }
  
  if (isHovered) {
    classes.push('error-hovered')
  }
  
  return classes.join(' ')
}

/**
 * Copy styles from source element to target element
 */
export function copyElementStyles(
  source: HTMLElement,
  target: HTMLElement,
  props: readonly string[] = TEXTAREA_STYLE_PROPS
): void {
  const computedStyle = window.getComputedStyle(source)
  props.forEach(prop => {
    target.style[prop as any] = computedStyle[prop as any]
  })
}

/**
 * Sync scroll position between two elements
 */
export function syncScrollPosition(
  source: HTMLElement,
  target: HTMLElement
): void {
  target.scrollTop = source.scrollTop
  target.scrollLeft = source.scrollLeft
}